import { useMemo, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Table } from '@factorialco/deprecated-design-system'
import {
  CreatePhaseLineItemDocument,
  CreateRoleLineItemDocument,
  CreateSimpleLineItemDocument,
  DeleteLineItemDocument,
  QuotePricingPageDocument,
  UpdatePhaseLineItemDocument,
  UpdateRoleLineItemDocument,
  UpdateSimpleLineItemDocument,
} from 'generated/resources/graphql'
import debounce from 'lodash/debounce'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { QuotePricingLineItem, profitPercentage } from 'modules/finance/components/Quotes/utils'

import {
  addItemToPhaseInTree,
  findItemInTree,
  flattenLineItems,
  removeItemFromTree,
  replaceItemInTree,
} from './utils'
import Header from './Header'
import Body from './_Body'

interface QuotePhasesTableProps {
  currency: string
  lineItems: QuotePricingLineItem[]
  isProductQuote: boolean
  isDraftStatus: boolean
  isJobCatalogTreeEnabled: boolean
  legalEntityCountry: string | undefined
  quoteId: number
}

const QuotePhasesTable = ({
  currency,
  lineItems,
  isProductQuote,
  isDraftStatus,
  isJobCatalogTreeEnabled,
  legalEntityCountry,
  quoteId,
}: QuotePhasesTableProps) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const apolloClient = useApolloClient()
  const [standaloneItems, setStandaloneItems] = useState<QuotePricingLineItem[]>(lineItems)
  const discountPercentage = 0 // TODO: quote.discountPercentage

  useUnsafeEffect(() => {
    setStandaloneItems(lineItems)
  }, [lineItems])

  // Simple line items
  const [createSimpleLineItem] = useMutation(CreateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [updateSimpleLineItem] = useMutation(UpdateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updateSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [deleteLineItem] = useMutation(DeleteLineItemDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  // Role line items
  const [createRoleLineItem] = useMutation(CreateRoleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createRoleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [updateRoleLineItem] = useMutation(UpdateRoleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updateRoleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  // Phase line items
  const [createPhaseLineItem] = useMutation(CreatePhaseLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createPhaseFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  const [updatePhaseLineItem] = useMutation(UpdatePhaseLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updatePhaseFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  const updateSimpleLineItemRef = useRef(updateSimpleLineItem)
  useUnsafeEffect(() => {
    updateSimpleLineItemRef.current = updateSimpleLineItem
  }, [updateSimpleLineItem])

  const updateRoleLineItemRef = useRef(updateRoleLineItem)
  useUnsafeEffect(() => {
    updateRoleLineItemRef.current = updateRoleLineItem
  }, [updateRoleLineItem])

  const updatePhaseLineItemRef = useRef(updatePhaseLineItem)
  useUnsafeEffect(() => {
    updatePhaseLineItemRef.current = updatePhaseLineItem
  }, [updatePhaseLineItem])

  const standaloneItemsRef = useRef(standaloneItems)
  useUnsafeEffect(() => {
    standaloneItemsRef.current = standaloneItems
  }, [standaloneItems])

  const update = async (updatedItem: QuotePricingLineItem) => {
    const currentItem = findItemInTree(standaloneItemsRef.current, updatedItem.id)
    if (currentItem == null) return { success: false, errors: [] }

    const {
      id,
      description,
      quantity,
      unitPriceCents,
      unitCostCents,
      type,
      oldJobCatalogLevelId,
      jobCatalogTreeLevelId,
      taxRateId,
    } = updatedItem

    let handled: ReturnType<typeof handleResult<unknown>>

    if (type === 'phase') {
      if (description == null) return { success: false, errors: [] }

      const result = await updatePhaseLineItemRef.current({
        variables: { id, description },
      })
      handled = handleResult(result, () => undefined)
    } else if (type === 'role') {
      if (quantity == null) return { success: false, errors: [] }

      const result = await updateRoleLineItemRef.current({
        variables: {
          id,
          quantity,
          oldLevelId: oldJobCatalogLevelId,
          treeLevelId: jobCatalogTreeLevelId,
        },
      })
      handled = handleResult(result, () => undefined)
    } else {
      if (
        description == null ||
        quantity == null ||
        unitPriceCents == null ||
        unitCostCents == null
      ) {
        return { success: false, errors: [] }
      }

      const result = await updateSimpleLineItemRef.current({
        variables: {
          id,
          discountPercentage,
          description,
          quantity,
          unitPriceCents,
          unitCostCents,
          taxRateId,
        },
      })
      handled = handleResult(result, () => undefined)
    }
    return handled
  }

  const debouncedUpdate = useMemo(
    () =>
      debounce(async (updatedItem: QuotePricingLineItem, outdatedItem: QuotePricingLineItem) => {
        const handled = await update(updatedItem)
        if (!handled.success) {
          revertOptimisticState(outdatedItem, 'update')
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.save_error'),
            time: 2000,
          })
        }
        // TODO: if success, we dont use the response to update state cause we refetch the page query
        // Review if we can avoid that refetching and updateState with the mutation response
      }, 1000),
    []
  )

  useUnsafeEffect(() => {
    return () => {
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate])

  const updateQuoteApolloCachedData = (items: QuotePricingLineItem[]) => {
    const flatLineItems = flattenLineItems(items).filter((item) => item.type !== 'phase')
    const baseAmountCents = flatLineItems.reduce((sum, item) => sum + item.basePriceCents, 0)
    const taxesTotalAmountCents = flatLineItems.reduce(
      (sum, item) =>
        sum +
        (item.basePriceCents - item.basePriceCents * discountPercentage) *
          (item.taxRatePercentage || 0),
      0
    )
    const totalAmountCents = baseAmountCents + taxesTotalAmountCents
    const baseCostCents = flatLineItems.reduce((sum, item) => sum + item.baseCostCents, 0)
    const totalHours = flatLineItems
      .filter((item) => item.quantityType === 'hours')
      .reduce((sum, item) => sum + item.quantity, 0)
    const totalProfitCents = totalAmountCents - baseCostCents
    const totalProfitPercentage = profitPercentage(totalProfitCents, baseAmountCents)

    const cachedData = apolloClient.readQuery({
      query: QuotePricingPageDocument,
      variables: { id: quoteId, companyId: Number(companyId) },
    })

    if (cachedData?.finance?.quote) {
      const quote = cachedData.finance.quote
      const normalizeForCache = (
        lineItemsToNormalize: QuotePricingLineItem[]
      ): typeof quote.financeLineItems =>
        lineItemsToNormalize.map((item) => ({
          ...item,
          financeLineItems: item.financeLineItems ? normalizeForCache(item.financeLineItems) : [],
        }))
      apolloClient.writeQuery({
        query: QuotePricingPageDocument,
        variables: { id: quoteId, companyId: Number(companyId) },
        data: {
          ...cachedData,
          finance: {
            ...cachedData.finance,
            quote: {
              ...quote,
              financeLineItems: normalizeForCache(items),
              baseAmountCents,
              totalAmountCents,
              baseCostCents,
              totalHours,
              taxesTotalAmountCents,
              totalProfitCents,
              totalProfitPercentage,
            },
          },
        },
      })
    }
  }

  const updateStateOptimistically = (newItems: QuotePricingLineItem[], updateQuote: boolean) => {
    setStandaloneItems(newItems)
    if (updateQuote) {
      updateQuoteApolloCachedData(newItems)
    }
  }

  const revertOptimisticState = (
    handledItem: QuotePricingLineItem,
    mutationType: 'create' | 'delete' | 'update'
  ) => {
    let newItems: QuotePricingLineItem[]
    if (mutationType === 'create') {
      newItems = removeItemFromTree(standaloneItems, handledItem.id)
    } else if (mutationType === 'delete') {
      newItems =
        handledItem.parentId != null
          ? addItemToPhaseInTree(standaloneItems, handledItem.parentId, handledItem)
          : [handledItem, ...standaloneItems]
    } else {
      newItems = replaceItemInTree(standaloneItems, handledItem.id, handledItem)
    }
    setStandaloneItems(newItems)
    updateQuoteApolloCachedData(newItems)
  }

  const buildUpdatedItem = (
    item: QuotePricingLineItem,
    newValues: Partial<QuotePricingLineItem>
  ): QuotePricingLineItem => {
    // TODO: calculate basePriceCents, baseCostCents, profitCents here
    return {
      ...item,
      ...newValues,
    }
  }

  const handleUpdateItem = async (itemId: number, newValues: Partial<QuotePricingLineItem>) => {
    const outdatedItem = findItemInTree(standaloneItems, itemId)
    if (!outdatedItem) return

    const updatedItem = buildUpdatedItem(outdatedItem, newValues)
    const updatedItems = replaceItemInTree(standaloneItems, itemId, updatedItem)
    updateStateOptimistically(updatedItems, updatedItem.type === 'simple')
    debouncedUpdate(updatedItem, outdatedItem)
  }

  const buildNewItem = (
    itemType: 'role' | 'simple' | 'phase',
    parentId?: number | null
  ): QuotePricingLineItem => {
    const isPhase = itemType === 'phase'
    return {
      id: Date.now() * -1,
      description: '-',
      quantity: isPhase ? 0 : 1,
      unitPriceCents: 0,
      baseCostCents: 0,
      basePriceCents: 0,
      unitCostCents: 0,
      taxRatePercentage: 0,
      baseProfitPercentage: 0,
      parentId: isPhase ? null : (parentId ?? null),
      quantityType: itemType === 'simple' ? 'units' : 'hours',
      taxRateId: null,
      type: itemType,
    }
  }

  const handleAddItem = async (itemType: 'role' | 'simple' | 'phase', parentId?: number | null) => {
    const newItem = buildNewItem(itemType, parentId)
    const newItems =
      itemType === 'phase' || parentId == null
        ? [...standaloneItems, newItem]
        : addItemToPhaseInTree(standaloneItems, parentId, newItem)
    updateStateOptimistically(newItems, false)
    const handled = await create(newItem)
    if (!handled.success) {
      revertOptimisticState(newItem, 'create')

      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
        time: 2000,
      })
    }
    // TODO: if success, we dont use the response to update state cause we refetch the page query
    // Review if we can avoid that refetching and updateState with the mutation response
  }

  const create = async (newItem: QuotePricingLineItem) => {
    if (newItem.type === 'simple') {
      if (
        newItem.quantity == null ||
        newItem.unitPriceCents == null ||
        newItem.unitCostCents == null
      ) {
        return { success: false, errors: [] }
      }
      const result = await createSimpleLineItem({
        variables: {
          description: newItem.description,
          discountPercentage,
          quantity: newItem.quantity,
          sourceId: quoteId,
          sourceType: 'quote',
          unitPriceCents: newItem.unitPriceCents,
          unitCostCents: newItem.unitCostCents,
          parentId: newItem.parentId,
        },
      })
      return handleResult(result, () => undefined)
    }

    if (newItem.type === 'phase') {
      const result = await createPhaseLineItem({
        variables: {
          description: newItem.description,
          sourceId: quoteId,
          sourceType: 'quote',
        },
      })
      return handleResult(result, () => undefined)
    }

    const result = await createRoleLineItem({
      variables: {
        quantity: newItem.quantity,
        sourceId: quoteId,
        sourceType: 'quote',
        parentId: newItem.parentId,
      },
    })
    return handleResult(result, () => undefined)
  }

  const handleDeleteItem = async (itemId: number) => {
    const deletedItem = findItemInTree(standaloneItems, itemId)
    if (!deletedItem) return

    const updatedItems = removeItemFromTree(standaloneItems, itemId)
    updateStateOptimistically(updatedItems, true)

    const result = await deleteLineItem({
      variables: {
        id: itemId,
      },
    })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      // TODO: if success, we dont use the response to update state cause we refetch the page query
      // Review if we can avoid that refetching and updateState with the mutation response
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.deleted_successfully'),
        time: 2000,
      })
    } else {
      revertOptimisticState(deletedItem, 'delete')
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  return (
    <Table autoWidth>
      <Header isDraftStatus={isDraftStatus} isProductQuote={isProductQuote} />
      <Body
        standaloneItems={standaloneItems}
        currency={currency}
        isDraftStatus={isDraftStatus}
        isProductQuote={isProductQuote}
        isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
        onUpdateLineItem={handleUpdateItem}
        onAddLineItem={handleAddItem}
        onDeleteLineItem={handleDeleteItem}
        legalEntityCountry={legalEntityCountry}
      />
    </Table>
  )
}

export default QuotePhasesTable
